import { isNotDefined } from '@sgme/fp';
import { DocumentError } from 'apiConnector/document/document';
import { DocumentFormat } from 'epic/common-form/Documentation';
import type { DocumentType } from 'models/documents/DocumentType';
import type { AppState } from 'state/state.model';
import { LoadableResult } from 'utils/status';

export const getDocumentDownloadError = (
  state: AppState,
  tabId: string,
  documentType: string | DocumentType,
  language: string,
  docFormat: DocumentFormat,
): DocumentError | undefined => {
  return state.documentDownload[tabId]?.[documentType]?.[language]?.[docFormat]?.error;
};

export const hasErrorForType = (state: AppState, tabId: string, documentType: string | DocumentType): boolean => {
  if (isNotDefined(state.documentDownload[tabId]?.[documentType])) {
    return false;
  }
  return Object.values(state.documentDownload[tabId][documentType]).some(languageDownloads =>
    Object.values(languageDownloads).some(downloadState => downloadState.status === 'error'),
  );
};

export const isDownloading = (
  state: AppState,
  tabId: string,
  documentType: string | DocumentType,
  language: string,
  docFormat: DocumentFormat,
): boolean => {
  if (isNotDefined(state.documentDownload[tabId]?.[documentType]?.[language]?.[docFormat])) {
    return false;
  }
  return state.documentDownload[tabId][documentType][language][docFormat].status === 'loading';
};

export const selectIsLanguageDocDownloading = (
  state: AppState,
  tabId: string,
  documentType: string | DocumentType,
  language: string,
): boolean => {
  if (isNotDefined(state.documentDownload[tabId]?.[documentType]?.[language])) {
    return false;
  }
  return Object.values(state.documentDownload[tabId][documentType][language] ?? {}).some(
    downloadState => downloadState.status === 'loading',
  );
};

const emptyArray: DocumentError[] = [];
export const selectLanguageDocErrors = (
  state: AppState,
  tabId: string,
  documentType: string | DocumentType,
  language: string,
): DocumentError[] => {
  if (isNotDefined(state.documentDownload[tabId]?.[documentType]?.[language])) {
    return emptyArray;
  }
  return Object.values(state.documentDownload[tabId][documentType][language])
    .filter(
      (downloadState): downloadState is LoadableResult<DocumentError> =>
        typeof downloadState === 'object' &&
        downloadState !== null &&
        'status' in downloadState &&
        downloadState.status === 'error',
    )
    .map(errorState => errorState.error)
    .filter((error): error is DocumentError => error !== undefined);
};

export const selectDocumentErrorCount = (state: AppState, tabId: string): number => {
  if (isNotDefined(state.documentDownload[tabId])) {
    return 0;
  }

  let errorCount = 0;

  Object.values(state.documentDownload[tabId]).forEach(documentTypes => {
    Object.values(documentTypes).forEach(languages => {
      Object.values(languages).forEach(formatDownloads => {
        if (formatDownloads.status === 'error') {
          errorCount++;
        }
      });
    });
  });

  return errorCount;
};
